import React, { useState, useEffect } from 'react';
import { FiEye, FiMail, FiTrash2, FiSearch, FiCheckCircle } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';

const ManageView = ({
  fileList,
  handleDelete,
  parseFilename,
  fieldLabels = [],
  fieldKeys = [],
  sortField,
  sortDirection,
  handleSort,
  API_URL,
  onEmail,
  selectedRow,
  setSelectedRow,
  canDelete = false
}) => {
  const [localQueued, setLocalQueued] = useState(() => new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delName, setDelName] = useState('');
  const [filterText, setFilterText] = useState(() => {
    try { return localStorage.getItem('manage.filterText') || ''; } catch (_) { return ''; }
  });
  // Pagination: rows per page persisted, current page in-memory
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('manage.rowsPerPage') || '50', 10);
      return Number.isFinite(v) && v > 0 ? v : 50;
    } catch (_) { return 50; }
  });
  const [page, setPage] = useState(0);

  useEffect(() => {
    try { localStorage.setItem('manage.filterText', filterText); } catch (_) {}
  }, [filterText]);

  useEffect(() => {
    try { localStorage.setItem('manage.rowsPerPage', String(rowsPerPage)); } catch (_) {}
  }, [rowsPerPage]);

  useEffect(() => {
    setLocalQueued((prev) => {
      if (!prev || prev.size === 0) return prev;
      const active = new Set(
        (Array.isArray(fileList) ? fileList : [])
          .filter(f => f && (f.processing || f.queued || f.forced))
          .map(f => f.filename)
      );
      let changed = false;
      const next = new Set();
      for (const name of prev) {
        if (active.has(name)) {
          next.add(name);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fileList]);

  const sortedFiles = [...fileList];
  const statusRank = { error: -1, processing: 0, queued: 1, missing: 2, ready: 3 };
  const getOcrStatus = (f) => {
    if (f.invalid || f.size === 0) return 'error';
    const isLocallyQueued = localQueued.has(f.filename);
    const hasText = !!f.hasText;
    return (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
  };

  if (sortField) {
    if (sortField === '__ocr__') {
      sortedFiles.sort((a, b) => {
        const sa = statusRank[getOcrStatus(a)] ?? 99;
        const sb = statusRank[getOcrStatus(b)] ?? 99;
        if (sa < sb) return sortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      sortedFiles.sort((a, b) => {
        const aVal = String(parseFilename(a.filename)[sortField] || '').toLowerCase();
        const bVal = String(parseFilename(b.filename)[sortField] || '').toLowerCase();
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
  }

  const t = filterText.trim().toLowerCase();
  const finalFiles = sortedFiles.filter(f => {
    if (!t) return true;
    const obj = parseFilename(f.filename);
    for (const k of fieldKeys) {
      if (String(obj[k] || '').toLowerCase().includes(t)) return true;
    }
    return false;
  });

  // Clamp/reset page when inputs change
  const totalPages = Math.max(1, Math.ceil(finalFiles.length / Math.max(1, rowsPerPage)));
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, filterText, sortField, sortDirection, finalFiles.length]);

  const startIdx = page * rowsPerPage;
  const endIdx = Math.min(finalFiles.length, startIdx + rowsPerPage);
  const pageFiles = finalFiles.slice(startIdx, endIdx);

  return (
    <div>
      <div style={titleRow}>
        <h2 style={{ margin: 0 }}>Manage Files</h2>
        <div style={filterWrapper}>
          <FiSearch aria-hidden style={filterIcon} />
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setFilterText(''); }}
            placeholder={`Filter by ${fieldLabels.join(', ')}`}
            style={filterInput}
          />
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              aria-label="Clear filter"
              title="Clear"
              style={filterClear}
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <table style={table}>
        <thead>
          <tr>
            <th style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1 }}>Actions</th>
            <th
              style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1, cursor: 'pointer' }}
              onClick={() => handleSort('__ocr__')}
              title="Sort by OCR status"
            >
              OCR{sortField === '__ocr__' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
            </th>
            {fieldKeys.map((field, idx) => (
              <th
                key={field}
                style={{ ...th, cursor: 'pointer' }}
                onClick={() => handleSort(field)}
              >
                {fieldLabels[idx] || field}
                {sortField === field && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageFiles.map((f, i) => {
            const globalIndex = startIdx + i;
            const parsed = parseFilename(f.filename);
            const isSelected = globalIndex === selectedRow;
            const baseRow = globalIndex % 2 === 0 ? rowEven : rowOdd;
            const rowStyle = isSelected ? { ...baseRow, ...rowSelected } : baseRow;
            return (
              <tr key={globalIndex} style={rowStyle} onClick={() => setSelectedRow && setSelectedRow(isSelected ? null : globalIndex)}>
                <td style={{ ...tdNarrow, whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
                    <a
                      href={`${API_URL}/files/${encodeURIComponent(f.filename)}?token=${encodeURIComponent((localStorage.getItem('auth_token')||''))}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-view file-action-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FiEye title="View" />
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); onEmail && onEmail(f.filename); }}
                      className="btn btn-primary file-action-btn"
                    >
                      <FiMail title="Email" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDelName(f.filename); setDelOpen(true); }}
                        className="btn btn-delete file-action-btn"
                        title="Move file to trash"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ ...tdNarrow, whiteSpace: 'nowrap', width: 1 }}>
                  {(() => {
                    if (f.invalid || f.size === 0) {
                      return (
                        <span
                          title="File is zero bytes or invalid"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color: '#b71c1c', fontSize: 12, border: '1px solid #b71c1c33', whiteSpace: 'nowrap', fontWeight: 600 }}
                        >
                          ! Error
                        </span>
                      );
                    }
                    const isLocallyQueued = localQueued.has(f.filename);
                    const hasText = !!f.hasText;
                    // Prioritize processing/queued over hasText so forced re-OCR doesn't show Ready
                    const status = (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
                    if (status === 'ready') {
                      const readyPillStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color: '#2e7d32', border: '1px solid #2e7d3233' };
                      return (
                        <span title="Text detected" style={readyPillStyle}>
                          <FiCheckCircle size={14} />
                        </span>
                      );
                    }
                    const color = status === 'processing' ? '#1565c0' : status === 'queued' ? '#ff8f00' : '#b71c1c';
                    const label = status === 'processing' ? 'OCR…' : status === 'queued' ? 'Queued' : 'No Text';
                    const clickable = status === 'missing';
                    const onClick = async (e) => {
                      e.stopPropagation();
                      if (!clickable) return;
                      try {
                        const res = await fetch(`${API_URL}/files/${encodeURIComponent(f.filename)}/ocr`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')||''}` },
                          body: JSON.stringify({ force: true })
                        });
                        if (res.ok) {
                          setLocalQueued(prev => new Set(prev).add(f.filename));
                        } else {
                          const j = await res.json().catch(() => ({}));
                          alert(j.message || 'Failed to enqueue OCR');
                        }
                      } catch (e) {
                        alert('Failed to enqueue OCR');
                      }
                    };
                      return (
                        <span
                          onClick={onClick}
                          title={clickable ? 'Click to OCR now' : undefined}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 60, padding: '2px 6px', borderRadius: 12, background: 'var(--ocr-pill-bg)', color, fontSize: 12, border: `1px solid ${color}33`, cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                        >
                          {label}
                        </span>
                      );
                  })()}
                </td>
                {fieldKeys.map((k) => (
                  <td key={k} style={td}>{parsed[k] || ''}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Pagination controls */}
      <div style={pagerRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="manage-rpp" style={{ opacity: 0.85 }}>Rows per page:</label>
          <select
            id="manage-rpp"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Math.max(1, parseInt(e.target.value || '50', 10) || 50)); setPage(0); }}
            style={pagerSelect}
          >
            {[25, 50, 100, 200].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div style={{ opacity: 0.9 }}>
          {finalFiles.length === 0
            ? 'No rows'
            : `Showing ${startIdx + 1}–${endIdx} of ${finalFiles.length}`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn" onClick={() => setPage(0)} disabled={page <= 0}>First</button>
          <button className="btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page <= 0}>Prev</button>
          <span style={{ minWidth: 110, textAlign: 'center' }}>Page {Math.min(page + 1, totalPages)} / {totalPages}</span>
          <button className="btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</button>
          <button className="btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</button>
        </div>
      </div>
      {canDelete && (
        <ConfirmModal
          open={delOpen}
          title="Move File to Trash"
          message={`Move "${delName}" to trash? You can restore it later from the Trash view.`}
          confirmLabel="Move to Trash"
          onCancel={() => setDelOpen(false)}
          onConfirm={async () => { try { await handleDelete(delName); } catch(_) {} setDelOpen(false); }}
        />
      )}
    </div>
  );
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 20,
  backgroundColor: 'var(--card-bg)'
};

const th = {
  textAlign: 'left',
  padding: '10px 8px',
  backgroundColor: 'var(--table-head-bg)',
  color: 'var(--text)',
  borderBottom: '2px solid var(--border)',
  fontSize: '14px',
  fontWeight: 'bold'
};

const td = {
  padding: '8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

const rowEven = { backgroundColor: 'var(--row-even-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowOdd = { backgroundColor: 'var(--row-odd-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowSelected = {
  backgroundColor: 'var(--row-selected-bg, rgba(100,181,246,0.15))',
  boxShadow: 'inset 0 0 0 2px rgba(100,181,246,0.35)',
};

const titleRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 8,
};

const filterInput = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  padding: '10px 36px 10px 36px',
  borderRadius: 8,
  border: '1.5px solid var(--primary)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
};

const filterWrapper = {
  position: 'relative',
  flex: 1,
  display: 'flex',
};

const filterIcon = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0.7,
  pointerEvents: 'none'
};

const filterClear = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border)',
  background: 'var(--hover-bg)',
  color: 'var(--text)',
  opacity: 0.9,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  borderRadius: 12,
  zIndex: 1,
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: '#1565c0',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
};

const deleteButton = {
  marginLeft: 10,
  padding: '6px 10px',
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const pagerRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 12,
  paddingTop: 10,
};

const pagerSelect = {
  padding: '6px 8px',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
};

export default ManageView;

// Compact column helpers
const thNarrow = { padding: '8px 8px' };
const tdNarrow = { padding: '6px 8px' };

// Legacy inline SVG icons removed in favor of react-icons
